import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { noop } from 'lodash-es';
import type { SpaceProps, WidthProps } from 'styled-system';
import { color, fontWeight, minWidth, themeGet } from 'styled-system';

import { focusOutlineCSS } from '../../theme/styles';
import { cursor } from '../../theme/system-utilities';
import { Flex } from '../Flex';
import { BodyRegularBR2, HeadlineH9 } from '../Typography';
import { CodeWithButton } from './CodeWithButton';
import { CodeWithCopy } from './CodeWithCopy';
import { INLINE_LAYOUT_TEXT_STYLE, INLINE_LAYOUT_VOUCHER_CODE_CONTAINER } from './styles';
import { VoucherBannerVariant } from './types';

const VoucherCodeText = styled(BodyRegularBR2)<any>``;

const voucherCodeTextStyles = {
  m: '0',
  as: 'p',
  textAlign: 'left',
};

const VoucherCodeContainer = styled(Flex)<any>`
  ${color};
  ${cursor};
  ${fontWeight};
  ${focusOutlineCSS};

  cursor: pointer;

  .tooltip > div {
    padding: ${themeGet('space.sp_8')}px;
    ${minWidth};
  }
`;

const voucherCodeContainerStyles: ComponentProps<typeof VoucherCodeContainer> = {
  position: 'relative',
  my: 'sp_4',
  px: ['sp_8', null, null, 'sp_12'],
  py: ['6px', null, null, 'sp_8'],
  borderRadius: 'medium',
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: [180, 200, 220],
  maxWidth: [null, '383px', null, '436px'],
};

interface CodeProps extends WidthProps, SpaceProps {
  onClick?: () => void;
  code?: string;
  label?: string;
  bgColor?: string;
  textColor?: string;
  displayFlexRow?: boolean;
  variant?: VoucherBannerVariant;
  voucherSave?: number;
  isVertical?: boolean;
}

const Code = ({
  onClick = noop,
  label,
  code,
  bgColor,
  textColor,
  displayFlexRow,
  variant = VoucherBannerVariant.Copy,
  voucherSave,
  isVertical,
  width,
  ...props
}: CodeProps) => {
  if (!code) {
    return null;
  }

  const codeText = (
    <VoucherCodeText
      data-testid="voucher-code"
      color={textColor}
      {...voucherCodeTextStyles}
      {...(displayFlexRow ? INLINE_LAYOUT_TEXT_STYLE : {})}
    >
      {label && `${label} `}
      <HeadlineH9 as="span" color="inherit">
        {code}
      </HeadlineH9>
    </VoucherCodeText>
  );

  const container = ({ children, ...containerProps }) => (
    <VoucherCodeContainer
      data-testid="voucher-code-container"
      backgroundColor={bgColor}
      {...voucherCodeContainerStyles}
      {...containerProps}
      {...props}
      {...(displayFlexRow ? INLINE_LAYOUT_VOUCHER_CODE_CONTAINER : {})}
    >
      {children}
    </VoucherCodeContainer>
  );

  const Variant = {
    [VoucherBannerVariant.Copy]: CodeWithCopy,
    [VoucherBannerVariant.Button]: CodeWithButton,
  }[variant];

  return (
    <Variant
      onClick={onClick}
      container={container}
      code={code}
      codeText={codeText}
      textColor={textColor}
      width={width}
    />
  );
};

export default Code;
